import { useMemo } from 'react';

import { type Item, RenderVariant, SegmentationTargets } from '../../types';

export type useAudienceSegmentationProps = {
	audienceSegmentationGroupIds?: string[];
	cards: Item[];
	isCompanyHub: boolean;
	isInViewMode: boolean;
	isSegmentationShown: boolean;
	isTargetAudience: (targetGroupIds: string[] | undefined) => boolean;
	renderVariant: RenderVariant;
	segmentationTarget?: SegmentationTargets;
};

type ReturnType = {
	filteredCards: Item[];
};

export const useAudienceSegmentation = ({
	isCompanyHub,
	isSegmentationShown,
	isTargetAudience,
	cards,
	isInViewMode,
	segmentationTarget,
	audienceSegmentationGroupIds,
	renderVariant,
}: useAudienceSegmentationProps): ReturnType => {
	const shouldApplySegmentation = useMemo(
		() => isCompanyHub && isSegmentationShown,
		[isCompanyHub, isSegmentationShown],
	);

	const filteredCards = useMemo(() => {
		const isAudienceSegmentationEnabled = isInViewMode && shouldApplySegmentation;
		const isDynamicOrManualWithAllTarget =
			renderVariant === RenderVariant.DYNAMIC ||
			(renderVariant === RenderVariant.MANUAL && segmentationTarget === SegmentationTargets.ALL);

		if (!isAudienceSegmentationEnabled) {
			return cards;
		}
		if (isDynamicOrManualWithAllTarget) {
			return isTargetAudience(audienceSegmentationGroupIds) ? cards : [];
		}
		return cards.filter((card) => isTargetAudience(card.audienceSegmentationGroupIds));
	}, [
		isInViewMode,
		shouldApplySegmentation,
		segmentationTarget,
		cards,
		renderVariant,
		isTargetAudience,
		audienceSegmentationGroupIds,
	]);

	return {
		filteredCards,
	};
};
